import React, { useState, useCallback, useEffect } from 'react';
import clsx from 'clsx';

import ExtendedTextField from '@src/stories/elements/ExtendedTextField/ExtendedTextField';
import ExtendedCheckbox from '@src/stories/elements/ExtendedCheckbox/ExtendedCheckbox';
import ExtendedTextareaField from '@src/stories/elements/ExtendedTextareaField/ExtendedTextareaField';
import ExtendedButton from '@src/stories/elements/ExtendedButton/ExtendedButton';
import ExtendedCustomSelectField from './ExtendedCustomSelectField';
import { updateItems } from "frontend/js/useShopifyCart";
import { debounce } from 'alpinejs/src/utils/debounce';
import { ProductType } from './CartEnums';

export default function ExtendedCartItemDetails ({
  item,
  itemIndex,
  hasAddOns,
  cardTypes,
  giftWrapTypes,
  optionsInCart,
  updatingCart,
  isGiftWrapEnabled,
  productsWithCardOptions,
  hasOtherItems,
  translations,
  onItemDelete,
  onApplyMessageToAll,
  lineItem,
  isHamper,
  characterLimit
}) {

  const [isLoading, setIsLoading] = useState(false);
  const [hasCard, setHasCard] = useState(!!item.properties._card_type);
  const [hasGiftWrap, setHasGiftWrap] = useState(!!item.properties._gift_wrap_type);
  const [emptyMessage, setEmptyMessage] = useState(item.properties._is_card_message_blank);

  useEffect(() => {
    if (item.properties._card_message && !item.properties['Card Message']) {
      item.properties['Card Message'] = item.properties._card_message;
    } else if (item.properties['Card Message'] && !item.properties._card_message) {
      item.properties._card_message = item.properties['Card Message'];
    }
  }, []);
  
  const showGiftWrap = isGiftWrapEnabled && hasAddOns && !isHamper && item.product_type !== ProductType.GIFT_CARDS;
  const showCard = productsWithCardOptions === 'hampers' ? isHamper && hasAddOns : item.product_type !== ProductType.GIFT_CARDS && hasAddOns;

  async function handleQuantityUpdate(item, itemIndex) {
    if (isLoading) return;

    setIsLoading(true);
    const updates = {}

    updates[`${item.key}`] = 0;

    if (item.properties._card_type) {
      const optionInCart = optionsInCart[item.properties._card_type];
      if (optionInCart) {
        optionInCart.quantity -= 1

        if (optionInCart.key) {
          updates[`${optionInCart.key}`] = optionInCart.quantity;
        }
      }
    }

    if (item.properties._gift_wrap_type) {
      const optionInCart = optionsInCart[item.properties._gift_wrap_type];

      if (optionInCart) {
        optionInCart.quantity -= 1

        if (optionInCart.key) {
          updates[`${optionInCart.key}`] = optionInCart.quantity;
        }
      }
    }

    await updateItems({ updates });

    onItemDelete(item.variant_id, itemIndex);
    setIsLoading(false);
  }

  const handleChange = useCallback(
    debounce((event, item) => {
      const value = event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value

      item.properties[event.target.name] = value;
      
      // Synchronize hidden _card_message with visible Card Message property
      if (event.target.name === '_card_message') {
        item.properties['Card Message'] = value;
        item.properties['_card_message'] = value;
      }
    }, 500), []
  );

  function handleCardTypeChange (value, item, name) {
    handleSelectChange(value, item, name);
    if(value == 0) {
      setHasCard(false);
    }
    else {
      setHasCard(!!value);
    }
  }

  function handleGiftWrapTypeChange (value, item, name) {
    handleSelectChange(value, item, name);
    if(value == 0) {
      setHasGiftWrap(false);
    }
    else {
      setHasGiftWrap(!!value);
    }
  }

  function handleSelectChange (value, item, name) {
    if (name == '_card_type' && value == 0) {
      item.properties['_card_message'] = '';
      item.properties['Card Message'] = ''; // Sync visible property
      item.properties['_is_card_message_blank'] = false;
      if (item.formErrors) {
        delete item.formErrors.card_message;
        delete item.formErrors.card_type;
      }
    }
    
    if (name == '_gift_wrap_type' && value == 0) {
      if (item.formErrors) {
        delete item.formErrors.gift_wrap_type;
      }
    }
    
    const previousValue = item.properties[name];
    
    if (item.properties[name] !== value) {
      if (value) {
        if (name === '_card_type') {
          const cardType = cardTypes.find(card => card.id === Number(value));
          if (!cardType || !cardType.available) {
            console.warn("Card not available:", value);
            return; 
          }
        }
        
        if (optionsInCart[value]) {
          if (!previousValue) {
            optionsInCart[value].quantity += 1;
          }
          optionsInCart[value].isUpdated = true;
          optionsInCart[value].associatedItems = optionsInCart[value].associatedItems || [];
          if (!optionsInCart[value].associatedItems.includes(item.key)) {
            optionsInCart[value].associatedItems.push(item.key);
          }
        } else {
          optionsInCart[value] = {
            id: value,
            quantity: 1,
            isNew: true,
            associatedItems: [item.key],
            properties: {}
          };
        }
      } 
      

      if (previousValue && optionsInCart[previousValue]) {

        if (optionsInCart[previousValue].associatedItems) {
          optionsInCart[previousValue].associatedItems = 
            optionsInCart[previousValue].associatedItems.filter(key => key !== item.key);
        }
        
        if (!optionsInCart[previousValue].associatedItems?.length) {
          optionsInCart[previousValue].quantity = 0;
          optionsInCart[previousValue].isUpdated = true;
        } else {
          optionsInCart[previousValue].quantity = Math.max(0, optionsInCart[previousValue].quantity - 1);
          optionsInCart[previousValue].isUpdated = true;
        }
      }

      item.properties[name] = value;
      
      if (name === '_card_type' && item.formErrors) {
        delete item.formErrors.card_message;
        delete item.formErrors.card_type;
      }
    }


  }

  const handleCheck = (event) => {
    const checked = event.target.checked;
    setEmptyMessage(checked);
    if (checked) {
      delete item.properties._card_message;
      delete item.properties['Card Message']; // Also remove visible property
    }
  }

  return (
    <div className='flex flex-col gap-4'>
      <hr className={clsx('bg-mb-neutral-500  my-2',{
        'max-lg:hidden': itemIndex === 0
      })} />
      
      <div className='flex items-center justify-between gap-4'>
        <div className='font-medium'>
          {translations.item} {itemIndex + 1}
        </div>
        

        <span
          className={clsx('text-2xl cursor-pointer', {
            'icon-trash': !isLoading,
            'icon-loader-alt animate-spin': isLoading
          })}
          onClick={() => handleQuantityUpdate(item, itemIndex)}
        ></span>
      </div>

      {Object.keys(item.properties || {}).some(key => key.startsWith('monogram')) && (
                  <div className='text-sm'>
                    <strong>Monogram:</strong> {item.properties['monogram-font']}, 
                    {item.properties['monogram-text-1'] && 
                      <span> {item.properties['monogram-text-1'].length > 10
                        ? `${item.properties['monogram-text-1'].substring(0, 10)}...` 
                        : item.properties['monogram-text-1']}</span>
                    }
                    {item.properties['monogram-text-2'] && 
                      <span>, {item.properties['monogram-text-2'].length > 10
                        ? `${item.properties['monogram-text-2'].substring(0, 10)}...` 
                        : item.properties['monogram-text-2']}</span>
                    }
                  </div>
        )}

        {item.properties['Messaging']  && 
            <div className='text-sm'>
                <strong> Messaging: </strong>
                <span>{item.properties['Messaging']}</span>
            </div>
        } 

        {item.properties['Uploaded Image']  && 
            <div className='text-sm'>
                <strong> Uploaded Image: </strong>
                <a target="_blank" className='break-all' href={item.properties['Uploaded Image'] }>{item.properties['Uploaded Image']}</a>
            </div>
        }  
            
        {Object.entries(item.properties || {})
          .filter(([key, value]) => key.startsWith('custom_') && value)
          .map(([key, value]) => {
              const label = key
                .replace(/^custom_/, '')
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase());
              return (
                <div className='text-sm' key={key}>
                  <strong>{label}:</strong> {value}
                </div>
              );
            })
          }

      <form
        className='flex flex-col gap-4'
        onSubmit={(event) => event.preventDefault()}
        onChange={(event) => handleChange(event, item)}
      > 

        <div className='flex flex-col lg:flex-col gap-4'>
          { isHamper &&  
            <div className='w-full flex flex-col lg:flex-row gap-4'> 
                { 
                <div className={`${hasAddOns ? 'w-full lg:w-1/2' : 'w-full'}`}>
                  <ExtendedTextField
                    name='_recipient_name'
                    label='Recipient name'
                    placeholder='Recipient name'
                    required
                    type="top-input"
                    key={`recipient_name_${item.properties._recipient_name}`}
                    value={item.properties._recipient_name}
                    hasError={item.formErrors?.recipient_name}
                    errorMessage={item.formErrors?.recipient_name}
                  />
                  </div>
                }
                {showCard && 
                  <div className={'w-full lg:w-1/2'}>
                    <div className='flex max-lg:flex-col gap-4 lg:gap-6'>
                    { <ExtendedCustomSelectField
                        name='_card_type'
                        placeholder='Select a card (optional)'
                        label={`Card ${itemIndex + 1}`}
                        options={cardTypes.filter(card => card.available)}
                        type="top-input"
                        disabled={isLoading || updatingCart}
                        key={`card_type_${item.properties._card_type}`}
                        value={item.properties._card_type}
                        hasError={item.formErrors?.card_type}
                        errorMessage={item.formErrors?.card_type}
                        className='flex-1'
                        lineItem={lineItem}
                        onChange={(value) => handleCardTypeChange(value, item, '_card_type')}
                      />
                    }
                    </div>
                  </div>    
                }
            </div>
          }
          { !isHamper &&  
            <div className='w-full flex flex-col lg:flex-row gap-4'> 
                { showGiftWrap  && 
                
                   <div className={`${ !showCard ? 'w-full' : 'w-full lg:w-1/2'}`}>
              
                        <ExtendedCustomSelectField
                          name='_gift_wrap_type'
                          label={`Gift wrapping ${itemIndex + 1}`}
                          options={giftWrapTypes}
                          lineItem={lineItem}
                          type="top-input"
                          placeholder='Select a gift wrap (optional)'
                          disabled={isLoading || updatingCart}
                          key={`gift_wrap_type_${item.properties._gift_wrap_type}`}
                          value={item.properties._gift_wrap_type}
                          hasError={item.formErrors?.gift_wrap_type}
                          errorMessage={item.formErrors?.gift_wrap_type}
                          className='flex-1'
                          onChange={(value) => handleGiftWrapTypeChange(value, item, '_gift_wrap_type')}
                        />
                  
                </div> }


                 {showCard && 
                  <div className={`${ !showGiftWrap ? 'w-full' : 'w-full lg:w-1/2'}`}>
                    <div className='flex max-lg:flex-col gap-4 lg:gap-6'>
                    { <ExtendedCustomSelectField
                        name='_card_type'
                        placeholder='Select a card (optional)'
                        label={`Card ${itemIndex + 1}`}
                        options={cardTypes.filter(card => card.available)}
                        type="top-input"
                        disabled={isLoading || updatingCart}
                        key={`card_type_${item.properties._card_type}`}
                        value={item.properties._card_type}
                        hasError={item.formErrors?.card_type}
                        errorMessage={item.formErrors?.card_type}
                        className='flex-1'
                        lineItem={lineItem}
                        onChange={(value) => handleCardTypeChange(value, item, '_card_type')}
                      />
                    }
                    </div>
                  </div>    
                }
            </div> 
          }
 
        </div>

        {hasCard &&
          <div>
            <div className='flex items-center justify-between gap-4 mb-2'>
              <div className='text-sm font-medium'>
                Type your message...
              </div>

              <ExtendedCheckbox
                className='item-details__blank-checkbox desktop'
                key={`is_card_message_blank_${item.properties._is_card_message_blank}`}
                checked={emptyMessage}
                onChange={(event)=> { handleCheck(event) }}
                disabled={isLoading || updatingCart}
                name='_is_card_message_blank'
                label='I would like it to be left blank'
              />
            </div>
            <div className="extended-cart_message-field"> 
              <ExtendedTextareaField
                name='_card_message'
                key={`card_message_${item.properties._card_message}`}
                disabled={ emptyMessage || isLoading || updatingCart}
                value={emptyMessage ? '' : item.properties._card_message }
                required={!item.properties._is_card_message_blank}
                maxCharacters={characterLimit}
                hasError={item.formErrors?.card_message}
                errorMessage={item.formErrors?.card_message}
                disableEmoji
                showCharactersLeft={false}
              />
            </div>

            <ExtendedCheckbox
              className='item-details__blank-checkbox mobile'
              key={`is_card_message_blank_${item.properties._is_card_message_blank}`}
              checked={emptyMessage}
              onChange={(event)=> { handleCheck(event) }}
              disabled={ emptyMessage || isLoading || updatingCart}
              name='_is_card_message_blank'
              label='I would like it to be left blank'
            />

            {hasOtherItems &&
              <ExtendedButton
                type='button'
                label='Apply message to all'
                style='secondary'
                className='mt-4'
                disabled={isLoading || updatingCart}
                onClick={() => onApplyMessageToAll(item.properties._card_message)}
              />
            }
          </div>
        }
        
      </form>
    </div>
  )
}